import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Slider, Switch,
  ToggleButtonGroup, ToggleButton, Chip, CircularProgress,
  Divider, FormControlLabel, Tab, Tabs, Link, alpha,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SchoolIcon from '@mui/icons-material/School';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MicIcon from '@mui/icons-material/Mic';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import PageHeader from '../../ui-components/PageHeader';

// ─── Constants ───────────────────────────────────────────────

const CATEGORY_COLORS = {
  Hook: '#ef4444', Structuur: '#3b82f6', Ritme: '#8b5cf6',
  Taal: '#10b981', Tempo: '#f59e0b', Complexiteit: '#ec4899', Beeld: '#06b6d4',
};

const CHANGE_COLORS = {
  modified: { bg: '#fef3c7', border: '#f59e0b' },
  added: { bg: '#d1fae5', border: '#10b981' },
  removed: { bg: '#fee2e2', border: '#ef4444' },
  unchanged: { bg: 'transparent', border: 'transparent' },
};

// ─── Tip Card Component ──────────────────────────────────────

const TipCard = ({ icon, title, children, color = '#3b82f6' }) => (
  <Paper sx={{
    p: 2.5, borderTop: `3px solid ${color}`, height: '100%',
    transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 },
  }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <span style={{ fontSize: '1.3rem' }}>{icon}</span> {title}
    </Typography>
    <Box sx={{ '& li': { mb: 0.5 }, '& ul': { pl: 2.5, m: 0 } }}>
      {children}
    </Box>
  </Paper>
);

// ─── Example Block ───────────────────────────────────────────

const ExampleBlock = ({ bad, good, label }) => (
  <Box sx={{ my: 1.5, fontSize: '0.85rem' }}>
    {label && <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>}
    {bad && (
      <Box sx={{ bgcolor: '#fee2e2', borderRadius: 1, p: 1, mb: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>
        <span style={{ color: '#dc2626', fontWeight: 600 }}>Niet: </span>{bad}
      </Box>
    )}
    {good && (
      <Box sx={{ bgcolor: '#d1fae5', borderRadius: 1, p: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
        <span style={{ color: '#059669', fontWeight: 600 }}>Wel: </span>{good}
      </Box>
    )}
  </Box>
);

// ─── Learn Tab ───────────────────────────────────────────────

const LearnTab = () => (
  <Box>
    {/* Hero */}
    <Paper sx={{
      p: 4, mb: 4, background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
      color: '#fff', borderRadius: 2,
    }}>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        De Explainer Gids
      </Typography>
      <Typography variant="body1" sx={{ maxWidth: 700, opacity: 0.9, mb: 2 }}>
        Alles wat je nodig hebt om een sterke explainer te maken. Gebaseerd op analyses van
        NOS op 3, Johnny Harris, Vox, Kurzgesagt en onze eigen Full Frame masterclass.
        43 video's, ~65.000 woorden scriptmateriaal geanalyseerd.
      </Typography>
      <Button
        variant="contained"
        color="inherit"
        sx={{ color: '#1e3a5f', fontWeight: 600 }}
        href="https://wiki.fullframe.nl/doc/analyse-nos-op-3-explainers-shorts-aQw54E7PFe/edit"
        target="_blank"
        endIcon={<OpenInNewIcon />}
      >
        Bekijk volledige analyse op Wiki
      </Button>
      <Button
        variant="outlined"
        color="inherit"
        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontWeight: 600, ml: 1, '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' } }}
        href="/Explainer_Bijbel.pdf"
        target="_blank"
        endIcon={<DownloadIcon />}
      >
        Explainer Bijbel (PDF)
      </Button>
    </Paper>

    {/* Proces */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Het Proces</Typography>
    <Box sx={{ display: 'flex', gap: 2, mb: 4, flexDirection: { xs: 'column', sm: 'row' } }}>
      {[
        { step: '1', title: 'Voorbereiding', color: '#3b82f6', items: ['Boodschap & doelgroep bepalen', 'Doel: informeren, overtuigen of gedrag veranderen?', 'Vorm kiezen (host, animatie, mix)', 'Script schrijven', 'Host & locatie(s) regelen'] },
        { step: '2', title: 'Productie', color: '#8b5cf6', items: ['Host op sterke locaties (Show & Tell)', 'Ruimte laten voor animatie in beeld', 'Handelingen meegeven aan host', 'Inhoud laten landen, niet overhaasten', 'Verschillende vormen per onderwerp'] },
        { step: '3', title: 'Post-productie', color: '#10b981', items: ['Animatie aanhaken bij script', 'Beeldwissel elke 3-5 seconden', 'Ritme: lang-kort-lang-kort', 'Sound design ondersteunt het verhaal', 'Ondertiteling (altijd!)'] },
      ].map(phase => (
        <Paper key={phase.step} sx={{ flex: 1, p: 2.5, borderTop: `3px solid ${phase.color}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%', bgcolor: phase.color,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '0.9rem',
            }}>
              {phase.step}
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>{phase.title}</Typography>
          </Box>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {phase.items.map((item, i) => (
              <li key={i}><Typography variant="body2">{item}</Typography></li>
            ))}
          </ul>
        </Paper>
      ))}
    </Box>

    {/* 8 Universele Wetten */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>De 8 Wetten van de Video-Explainer</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Wat NOS op 3, Johnny Harris, Vox en Kurzgesagt <b>allemaal</b> doen — de ijzeren regels.
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 1.5, mb: 4 }}>
      {[
        { n: 1, title: 'De hook is heilig', desc: 'Nooit openen met uitleg. Eerste 10-30 sec = emotie, verrassing, paradox of concrete situatie.', color: '#ef4444' },
        { n: 2, title: 'Eén centraal idee', desc: 'Als je niet in één zin kunt zeggen waar de video over gaat, is het te breed.', color: '#3b82f6' },
        { n: 3, title: 'Kijker is nooit passief', desc: 'Min. 3-5 momenten van directe aanspreking, retorische vragen of gedeelde ontdekking.', color: '#8b5cf6' },
        { n: 4, title: 'Ritme door afwisseling', desc: 'Lang-kort-lang-kort. Drie lange zinnen achter elkaar = publiek verliest grip.', color: '#10b981' },
        { n: 5, title: 'Concreet verslaat abstract', desc: 'Elk abstract concept heeft een fysieke vertaling nodig. Paperclip = kernenergie.', color: '#f59e0b' },
        { n: 6, title: 'Bronnen in het verhaal', desc: 'Geen voetnoten. Data en bronnen casual verweven in de vertelling.', color: '#06b6d4' },
        { n: 7, title: 'Visuele cues in tekst', desc: '"Kijk", "dit", "let me show you" — de tekst stuurt het beeld. Script = shot list.', color: '#ec4899' },
        { n: 8, title: 'Afsluiting met lading', desc: 'Nooit neutraal eindigen. Circulair / urgent / kosmisch / expert-reflectie.', color: '#7c3aed' },
      ].map(law => (
        <Paper key={law.n} sx={{
          p: 2, borderTop: `3px solid ${law.color}`,
          transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '50%', bgcolor: law.color,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '0.75rem', flexShrink: 0,
            }}>
              {law.n}
            </Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>{law.title}</Typography>
          </Box>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{law.desc}</Typography>
        </Paper>
      ))}
    </Box>

    {/* Script Tips — de kern */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Script Schrijven</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 4 }}>

      <TipCard icon="🎣" title="De Hook" color="#ef4444">
        <Typography variant="body2" gutterBottom>
          De eerste 3 seconden bepalen of iemand blijft kijken. Open <b>nooit</b> met "In deze video gaan we het hebben over..."
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 0.5 }}>5 bewezen hook-types:</Typography>
        <ul>
          <li><Typography variant="body2"><b>Verrassende contradictie:</b> "Iran en de VS waren ooit goede vrienden."</Typography></li>
          <li><Typography variant="body2"><b>Persoonlijk scenario:</b> "Stel je voor dat je niet meer mag werken zonder zijn toestemming."</Typography></li>
          <li><Typography variant="body2"><b>Visueel commando:</b> "Kijk naar dit lijntje." / "Kijk deze beelden."</Typography></li>
          <li><Typography variant="body2"><b>Pop-cultuur referentie:</b> "Een glazen bol zoals de Palantir in Lord of the Rings..."</Typography></li>
          <li><Typography variant="body2"><b>"Ik ben hier":</b> "I'm in Washington DC and I want to show you..." (Harris-stijl)</Typography></li>
        </ul>
        <ExampleBlock
          bad='"In deze video leggen we uit hoe zonne-energie werkt."'
          good='"Wat als ik je vertelde dat je dak genoeg stroom kan opwekken om je hele straat te voeden?"'
        />
      </TipCard>

      <TipCard icon="🎵" title="Ritme & Zinslengte" color="#8b5cf6">
        <Typography variant="body2" gutterBottom>
          Het ritme is het geheim van goede explainers. Wissel <b>systematisch</b> af:
        </Typography>
        <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1.5, fontFamily: 'monospace', fontSize: '0.8rem', my: 1 }}>
          LANG (20-30 woorden) → uitleg, context<br/>
          KORT (2-8 woorden) → punchline, nadruk<br/>
          LANG → vervolg, nieuw punt<br/>
          KORT → reactie, oordeel
        </Box>
        <ExampleBlock
          label="Voorbeeld (NOS op 3 — Iran):"
          good={`"In het diepste geheim wordt een masterplan gesmeed om van premier Mossadek af te komen." [16 woorden]\n"Operatie Ajax." [2 woorden]`}
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Gemiddelde: <b>10-15 woorden</b> per zin (NOS) of <b>15-20</b> (Harris). Maar het gaat om de <i>afwisseling</i>.
        </Typography>
      </TipCard>

      <TipCard icon="🗣️" title="Taal & Register" color="#10b981">
        <Typography variant="body2" gutterBottom>
          Schrijf voor het <b>oor</b>, niet voor papier. Lees je script altijd hardop voor.
        </Typography>
        <ul>
          <li><Typography variant="body2"><b>Directe aanspreking:</b> "jij/je" — de kijker is deelnemer</Typography></li>
          <li><Typography variant="body2"><b>Actief, nooit passief:</b> "De CIA zorgt voor..." niet "Er werd gezorgd..."</Typography></li>
          <li><Typography variant="body2"><b>Geen jargon zonder uitleg:</b> "Femicide. Vrouwenmoord."</Typography></li>
          <li><Typography variant="body2"><b>Herkenbare vergelijkingen:</b> "Een getrouwde vrouw was een soort kleuter"</Typography></li>
          <li><Typography variant="body2"><b>Gebruik makkelijke taal</b> — het moet kloppen, maar iedereen moet het snappen</Typography></li>
        </ul>
        <ExampleBlock
          bad='"De implementatie van het beleid resulteerde in onvoorziene consequenties."'
          good='"Het ging mis. Flink mis."'
        />
      </TipCard>

      <TipCard icon="❓" title="Retorische Vragen" color="#f59e0b">
        <Typography variant="body2" gutterBottom>
          Retorische vragen zijn het <b>navigatiemiddel</b> van explainers. Ze doen drie dingen:
        </Typography>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          <li><Typography variant="body2"><b>Markeren overgangen</b> tussen onderwerpen</Typography></li>
          <li><Typography variant="body2"><b>Creëren spanning</b> — de kijker wil het antwoord weten</Typography></li>
          <li><Typography variant="body2"><b>Activeren de kijker</b> — van passief naar actief luisteren</Typography></li>
        </ol>
        <Typography variant="body2" sx={{ mt: 1 }}>Gebruik <b>4-6 per video</b> (explainer) of <b>max 1</b> (Short).</Typography>
        <Box sx={{ bgcolor: '#fffbeb', borderRadius: 1, p: 1.5, mt: 1, fontSize: '0.85rem', fontStyle: 'italic' }}>
          "Maar wie controleert of dat echt de juiste doelen zijn?"<br/>
          "Mag misdaad lonen of zijn klantgegevens heilig?"<br/>
          "Klinkt logisch, toch? Niet helemaal."
        </Box>
      </TipCard>

      <TipCard icon="🎬" title="Beeldscripting" color="#06b6d4">
        <Typography variant="body2" gutterBottom>
          Een explainer-script is ook een <b>beeldscript</b>. Schrijf wat de kijker ziet.
        </Typography>
        <ul>
          <li><Typography variant="body2"><b>Beeldwissel elke 3-5 sec</b> — elk nieuw feit = nieuw beeld</Typography></li>
          <li><Typography variant="body2"><b>"Kijk" en "dit"</b> als cues voor beeldwissels</Typography></li>
          <li><Typography variant="body2"><b>Draai de zin soms om</b> — dan kun je al iets laten zien</Typography></li>
          <li><Typography variant="body2"><b>7 beeldlagen:</b> presenter, archief, grafiek, nieuwsfragment, interview, social media, bedrijfsmateriaal</Typography></li>
          <li><Typography variant="body2"><b>Show & Tell:</b> kies locaties die de inhoud ondersteunen</Typography></li>
          <li><Typography variant="body2"><b>Ruimte voor animatie:</b> haak animatie aan bij het script, niet erna</Typography></li>
        </ul>
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
          Tip: Voeg [BEELD: ...] annotaties toe in je script zodat het montage-team weet wat je bedoelt.
        </Typography>
      </TipCard>

      <TipCard icon="🔄" title="Mini-Cliffhangers" color="#ec4899">
        <Typography variant="body2" gutterBottom>
          Houd de aandacht vast met <b>micro-wendingen</b> door het hele script:
        </Typography>
        <Box sx={{ bgcolor: '#fdf2f8', borderRadius: 1, p: 1.5, my: 1, fontSize: '0.85rem' }}>
          "Er is hoop dat de relatie verbetert. <b>Maar dan...</b>"<br/><br/>
          "Nobele doelen, toch? <b>Maar dan die andere kant.</b>"<br/><br/>
          "En dat, <b>je voelt er misschien aankomen</b>, vinden ze een iets minder goed idee."<br/><br/>
          "Maar wacht nog heel even met commenten."
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Harris-variant: <b>"Oh, and..."</b>, <b>"Okay wait. Pause."</b>, <b>"This is crazy."</b>
        </Typography>
      </TipCard>
    </Box>

    {/* Stijlen vergelijking */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Stijlen Vergelijken</Typography>
    <Paper sx={{ p: 2.5, mb: 4, overflowX: 'auto' }}>
      <Box component="table" sx={{
        width: '100%', borderCollapse: 'collapse',
        '& th, & td': { p: 1.5, textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem' },
        '& th': { fontWeight: 700, bgcolor: '#f8fafc', whiteSpace: 'nowrap' },
      }}>
        <thead>
          <tr>
            <th>Aspect</th>
            <th style={{ color: '#2563eb' }}>NOS op 3</th>
            <th style={{ color: '#7c3aed' }}>Johnny Harris</th>
            <th style={{ color: '#ef4444' }}>Vox</th>
            <th style={{ color: '#f59e0b' }}>Kurzgesagt</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Positie', 'Uitlegger ("dit zit zo")', 'Gids/ontdekker ("join me")', 'Onderzoeker ("I found...")', 'Kosmische verteller ("we")'],
            ['Perspectief', '2e/3e persoon', '1e persoon ("I/let me")', '1e persoon + experts', 'Anonieme voice-over, "wij"'],
            ['Zinslengte', '10-15 woorden gem.', '15-20 woorden gem.', '~16 woorden gem.', '14-18 woorden gem.'],
            ['Toon', 'Gepolijst, compact', 'Bewust imperfect', 'Conversationeel-informatief', 'Toegankelijk academisch'],
            ['Kernmiddel', 'Mini-cliffhangers', 'Persoonlijke anekdotes', '"But"-pivot, data-viz', 'Concrete analogieën'],
            ['Humor', 'Ironie, understatement', 'Nerd-humor, zelfspot', 'Droge observaties', 'Spaarzaam, understatement'],
            ['Visueel', 'Archief + grafiek', 'Animaties + locatie', 'Kaarten + data-viz', 'Volledig geanimeerd'],
            ['Afsluiting', 'Circulair / open vraag', 'Urgente oproep tot actie', 'Open expert-reflectie', 'Kosmisch perspectief'],
            ['Lengte', '5-12 min', '10-25 min', '8-17 min', '8-15 min'],
            ['Sterkste punt', 'Ritme & helderheid', 'Storytelling & urgentie', 'Data & experts', 'Perspectief & analogieën'],
          ].map(([aspect, nos, harris, vox, kurzgesagt], i) => (
            <tr key={i}>
              <td style={{ fontWeight: 600 }}>{aspect}</td>
              <td>{nos}</td>
              <td>{harris}</td>
              <td>{vox}</td>
              <td>{kurzgesagt}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Paper>

    {/* Modellen */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Modellen & Frameworks</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 4 }}>
      <TipCard icon="📺" title="YouTube Model" color="#ef4444">
        <Typography variant="body2" gutterBottom>
          Elke succesvolle YouTube-video levert minstens twee van deze vier:
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
          {[
            { label: 'Informatie', desc: 'Ik leer iets nieuws', color: '#3b82f6' },
            { label: 'Inspiratie', desc: 'Ik wil iets gaan doen', color: '#8b5cf6' },
            { label: 'Ontspanning', desc: 'Ik vergeet even alles', color: '#10b981' },
            { label: 'Geluk', desc: 'Ik voel me goed', color: '#f59e0b' },
          ].map(item => (
            <Box key={item.label} sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1.5, borderLeft: `3px solid ${item.color}` }}>
              <Typography variant="body2" fontWeight={700}>{item.label}</Typography>
              <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          Explainers scoren altijd op <b>Informatie</b>. De beste ook op Inspiratie of Ontspanning.
        </Typography>
      </TipCard>

      <TipCard icon="🧠" title="CASI Model (gedragsverandering)" color="#8b5cf6">
        <Typography variant="body2" gutterBottom>
          Als je doel <b>gedragsverandering</b> is (bijv. veiligheid, compliance, gezondheid), gebruik het CASI-model:
        </Typography>
        <ul>
          <li><Typography variant="body2"><b>C</b>apability — Kan de kijker het? (kennis, vaardigheden)</Typography></li>
          <li><Typography variant="body2"><b>A</b>ttitude — Wil de kijker het? (motivatie, overtuiging)</Typography></li>
          <li><Typography variant="body2"><b>S</b>ociale norm — Doen anderen het ook? (sociale druk)</Typography></li>
          <li><Typography variant="body2"><b>I</b>ntentie — Gaat de kijker het echt doen? (concrete stap)</Typography></li>
        </ul>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Eindig altijd met een <b>concrete handeling</b>: "Ga naar...", "Vraag je leidinggevende om..."
        </Typography>
      </TipCard>
    </Box>

    {/* Beslisboom */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Wanneer Welke Stijl?</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Kies de juiste aanpak op basis van je doel, afzender en doelgroep.
    </Typography>
    <Paper sx={{ p: 2.5, mb: 4, overflowX: 'auto' }}>
      <Box component="table" sx={{
        width: '100%', borderCollapse: 'collapse',
        '& th, & td': { p: 1.5, textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem' },
        '& th': { fontWeight: 700, bgcolor: '#f8fafc', whiteSpace: 'nowrap' },
      }}>
        <thead>
          <tr><th>Vraag</th><th>Situatie</th><th>Aanpak</th></tr>
        </thead>
        <tbody>
          {[
            ['Doel?', 'Informeren', 'NOS op 3 als basis'],
            ['', 'Overtuigen / gedragsverandering', 'Harris als basis'],
            ['', 'Transformeren / nieuw perspectief', 'Kurzgesagt als basis'],
            ['', 'Verdiepen / complexiteit tonen', 'Vox als basis'],
            ['Afzender?', 'Organisatie / institutioneel', 'NOS-toon (gepolijst, journalistiek)'],
            ['', 'Individu / thought leader', 'Harris (eerste persoon, gids)'],
            ['', 'Merk zonder gezicht / tijdloos', 'Kurzgesagt (anonieme narrator)'],
            ['', 'Onderzoeksteam / redactie', 'Vox (ensemble + gids)'],
            ['Complexiteit?', 'Breed toegankelijk', 'NOS-ritme (kortste zinnen)'],
            ['', 'Technisch / wetenschappelijk', 'Kurzgesagt-escalatie'],
            ['', 'Politiek / historisch', 'Vox chronologische tijdlijn'],
            ['', 'Persoonlijk + complex', 'Harris met check-ins'],
          ].map(([vraag, situatie, aanpak], i) => (
            <tr key={i}>
              <td style={{ fontWeight: vraag ? 700 : 400, color: vraag ? '#1e3a5f' : 'transparent' }}>{vraag || '—'}</td>
              <td>{situatie}</td>
              <td style={{ fontWeight: 600 }}>{aanpak}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Paper>

    {/* Full Frame toepassingen */}
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Full Frame Toepassingen</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 4 }}>
      {[
        { title: 'B-to-B communicatie', color: '#2563eb', items: ['Basis: NOS-structuur (gepolijst, journalistiek)', 'Toevoegen: Vox expert-bevestiging', 'Zinslengte: 10-15 woorden', 'Conclusie: open vraag of circulaire terugkoppeling'] },
        { title: 'Employee / employer branding', color: '#8b5cf6', items: ['Basis: Harris-gids of NOS-journalistiek', 'Toevoegen: Kurzgesagt emotionele boog', 'Humor: NOS-stijl, spaarzaam als ventiel', 'Conclusie: urgentie (Harris) of hoop (Kurzgesagt)'] },
        { title: 'Thought leadership', color: '#7c3aed', items: ['Basis: Harris thesis-driven structuur', 'Toevoegen: Vox locatie + expert-bevestiging', 'Toon: bewust imperfect (Harris-authenticiteit)', 'Formaat: serialized reeks van 3-4 afleveringen'] },
        { title: 'Short / social media', color: '#ef4444', items: ['Basis: NOS-Short (1 feit, directe opening)', 'Toevoegen: Harris fysieke demonstratie', 'Zinslengte: max 8-10 woorden', 'CTA naar langere content'] },
      ].map(item => (
        <Paper key={item.title} sx={{ p: 2, borderLeft: `4px solid ${item.color}` }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{item.title}</Typography>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {item.items.map((line, i) => (
              <li key={i}><Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{line}</Typography></li>
            ))}
          </ul>
        </Paper>
      ))}
    </Box>

    {/* Script Templates */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Script Templates</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Vijf kant-en-klare structuren. Klik om de template te openen.
    </Typography>
    <Box sx={{ mb: 4 }}>
      {[
        {
          name: 'Template A: NOS-structuur', subtitle: 'Informeren', color: '#2563eb',
          steps: [
            { stap: 'Hook', timing: '0-20 sec', inhoud: 'Emotionele haak / verrassend feit / herkenbare situatie' },
            { stap: 'Belofte', timing: '20-40 sec', inhoud: 'Eén zin: wat gaat de kijker leren?' },
            { stap: 'Body', timing: 'Blokken van ~2 min', inhoud: 'Thematisch of chronologisch + retorische vragen als navigatie' },
            { stap: 'Cliffhangers', timing: 'Tussendoor', inhoud: '"Maar dan..." — wending die de kijker vasthoudt' },
            { stap: 'Outro', timing: 'Laatste 20 sec', inhoud: 'Circulaire terugkoppeling of open vraag' },
          ],
        },
        {
          name: 'Template B: Harris-structuur', subtitle: 'Overtuigen', color: '#7c3aed',
          steps: [
            { stap: 'Opening', timing: '0-15 sec', inhoud: 'Persoonlijke stelling of locatie-demonstratie' },
            { stap: 'Thesis', timing: '15-30 sec', inhoud: '"Dit is wat ik ga aantonen"' },
            { stap: 'Body', timing: 'Argument-gedreven', inhoud: 'Stelling → bewijs → tegenargument → meer bewijs' },
            { stap: 'Check-ins', timing: 'Tussendoor', inhoud: '"Okay, laten we even pauzeren" bij complexiteit' },
            { stap: 'Outro', timing: 'Laatste 30 sec', inhoud: 'Urgentie — "Dit is zeer mogelijk. Nu."' },
          ],
        },
        {
          name: 'Template C: Kurzgesagt-structuur', subtitle: 'Transformeren', color: '#d97706',
          steps: [
            { stap: 'Hook', timing: '0-20 sec', inhoud: 'Paradox of existentiële vraag' },
            { stap: 'Context', timing: '20-60 sec', inhoud: 'Waarom dit ertoe doet, verankerd in het dagelijkse' },
            { stap: 'Blok 1', timing: '~2 min', inhoud: 'Basisconcept uitgelegd met analogie' },
            { stap: 'Pivot', timing: '5 sec', inhoud: '"But..." of retorische vraag' },
            { stap: 'Blok 2 + 3', timing: '~4 min', inhoud: 'Diepere laag → verrassende wending of volledig plaatje' },
            { stap: 'Reflectie', timing: 'Laatste 45 sec', inhoud: 'Kosmisch perspectief of eerlijke conclusie' },
          ],
        },
        {
          name: 'Template D: Vox-structuur', subtitle: 'Verdiepen', color: '#ef4444',
          steps: [
            { stap: 'Hook', timing: '0-30 sec', inhoud: 'Concreet getal / locatie / incident / persoonlijke ontdekking' },
            { stap: 'Kernvraag', timing: '30-60 sec', inhoud: 'Expliciete vraag die het script stuurt' },
            { stap: 'Body', timing: 'Kroniek met tussenstops', inhoud: 'Narrator + expert + data + persoonlijke observatie' },
            { stap: 'Adempauze', timing: 'Halverwege', inhoud: 'Persoonlijke observatie of break als rustpunt' },
            { stap: 'Outro', timing: 'Laatste 60-90 sec', inhoud: 'Expert-quote die het onderwerp groter maakt' },
          ],
        },
        {
          name: 'Template E: Short', subtitle: '60-90 sec, elke stijl', color: '#10b981',
          steps: [
            { stap: 'Opening', timing: '0-3 sec', inhoud: '1 zin / visueel commando / directe stelling — max 10 woorden' },
            { stap: 'Kern', timing: '3-50 sec', inhoud: 'Eén vraag beantwoorden / één feit uitleggen / één mechanisme' },
            { stap: 'Kicker', timing: 'Laatste 5-10 sec', inhoud: 'CTA naar langere content of urgente afsluiting' },
          ],
        },
      ].map(template => (
        <Accordion key={template.name} sx={{ mb: 1, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8fafc', borderLeft: `4px solid ${template.color}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>{template.name}</Typography>
              <Chip label={template.subtitle} size="small" sx={{ fontWeight: 600, bgcolor: template.color, color: '#fff', fontSize: '0.7rem' }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse',
              '& th, & td': { p: 1.5, textAlign: 'left', borderBottom: '1px solid #f0f0f0', fontSize: '0.85rem' },
              '& th': { fontWeight: 700, bgcolor: '#fafbfc', width: '15%' },
            }}>
              <thead>
                <tr><th>Stap</th><th style={{ width: '20%' }}>Timing</th><th>Inhoud</th></tr>
              </thead>
              <tbody>
                {template.steps.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{s.stap}</td>
                    <td style={{ color: '#6b7280', fontSize: '0.8rem' }}>{s.timing}</td>
                    <td>{s.inhoud}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>

    {/* Formaat-overzicht */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Formaat Kiezen</Typography>
    <Paper sx={{ p: 2.5, mb: 4, overflowX: 'auto' }}>
      <Box component="table" sx={{
        width: '100%', borderCollapse: 'collapse',
        '& th, & td': { p: 1.5, textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem' },
        '& th': { fontWeight: 700, bgcolor: '#f8fafc' },
      }}>
        <thead>
          <tr><th>Formaat</th><th>Duur</th><th>Woorden</th><th>Geschikt voor</th><th>Structuur</th></tr>
        </thead>
        <tbody>
          {[
            ['Short / Reels', '30-60 sec', '140-220', 'Eén feit, teaser, nieuwsgierigheid wekken', '1 zin hook → uitleg → CTA'],
            ['Korte explainer', '3-5 min', '450-700', 'Eén kwestie uitleggen, actueel nieuws', 'Hook → 3 blokken → open einde'],
            ['Lange explainer', '8-15 min', '1200-2000', 'Achtergrondverhaal, "hoe is dit zo gekomen"', 'Hook → chronologisch → circulaire outro'],
            ['Serie (3-4 delen)', '4-6 min/deel', '400-600/deel', 'Complex thema opsplitsen, retentie', 'Cliffhanger per deel, doorverwijzing'],
          ].map(([format, dur, words, suited, struct], i) => (
            <tr key={i}>
              <td style={{ fontWeight: 600 }}>{format}</td>
              <td>{dur}</td>
              <td>{words}</td>
              <td>{suited}</td>
              <td>{struct}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Paper>

    {/* Quick checklist */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Script Checklist</Typography>
    <Paper sx={{ p: 2.5, mb: 4 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.5 }}>
        {[
          'Hook in de eerste 3 seconden (geen "In deze video...")',
          'Eén-zin belofte direct na de hook',
          'Script hardop voorgelezen en getimed',
          'Gemiddelde zinslengte 10-15 woorden',
          'Nooit 3 lange zinnen achter elkaar',
          'Retorische vragen bij overgangen (4-6x)',
          'Directe aanspreking (jij/je)',
          'Geen jargon zonder uitleg',
          'Mini-cliffhangers ("Maar dan...")',
          '[BEELD: ...] annotaties bij visuele momenten',
          'Circulaire outro of urgente afsluiting',
          'Expert/bron vermeld zonder saai te worden',
          'Werk met experts — het moet kloppen',
          'Vorm ondersteunt, leidt niet af',
          'Concrete handeling/CTA aan het eind',
          'Animatie-team aangehaakt bij scriptfase',
        ].map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 700, flexShrink: 0 }}>
              {String.fromCharCode(9744)}
            </Typography>
            <Typography variant="body2">{item}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>

    {/* Referentiedata */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Referentiedata per Kanaal</Typography>
    <Paper sx={{ p: 2.5, mb: 4, overflowX: 'auto' }}>
      <Box component="table" sx={{
        width: '100%', borderCollapse: 'collapse',
        '& th, & td': { p: 1.5, textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem' },
        '& th': { fontWeight: 700, bgcolor: '#f8fafc', whiteSpace: 'nowrap' },
      }}>
        <thead>
          <tr>
            <th>Metriek</th>
            <th style={{ color: '#2563eb' }}>NOS op 3</th>
            <th style={{ color: '#7c3aed' }}>J. Harris</th>
            <th style={{ color: '#ef4444' }}>Vox</th>
            <th style={{ color: '#d97706' }}>Kurzgesagt</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Gem. zinslengte', '10-15 woorden', '15-20 woorden', '~16 woorden', '14-18 woorden'],
            ['Spreektempo', '~150 wpm', '~160 wpm', '168 wpm (142-197)', '160-170 wpm'],
            ['Retorische vragen', '4-6 per video', '3-5 per video', '~4,3 per video', '3-5 per video'],
            ['Experts per video', '0-6 clips (voice-over)', '0-2 clips (audio)', '2-3 (on-camera)', '0 (alleen narrator)'],
            ['Korte explainer', '450-924 woorden', '450-924 woorden', '1.300-1.800 woorden', '1.300-1.800 woorden'],
            ['Lange explainer', '~1.500 woorden', '2.000-2.800 woorden', '2.000-2.800 woorden', '~1.500 woorden'],
            ['Short', '140-220 woorden', '140-220 woorden', 'n.v.t.', '210-234 woorden'],
            ['"But"-frequentie', 'Cliffhanger-stijl', 'Cliffhanger-stijl', '~6x per video', 'Elke 4-6 zinnen'],
          ].map(([metriek, nos, harris, vox, kurzgesagt], i) => (
            <tr key={i}>
              <td style={{ fontWeight: 600 }}>{metriek}</td>
              <td>{nos}</td>
              <td>{harris}</td>
              <td>{vox}</td>
              <td>{kurzgesagt}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Paper>

    {/* Inspiratie */}
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Inspiratie</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
      {[
        { name: 'NOS op 3', desc: 'Nederlandse uitlegvideo\'s over actualiteit', url: 'https://www.youtube.com/@NOSop3', color: '#2563eb' },
        { name: 'Johnny Harris', desc: 'Persoonlijke diepgravende explainers', url: 'https://www.youtube.com/@johnnyharris', color: '#7c3aed' },
        { name: 'Het Klokhuis', desc: 'Complexe onderwerpen simpel uitgelegd', url: 'https://www.youtube.com/@hetklokhuis', color: '#059669' },
        { name: 'Kurzgesagt', desc: 'Animatie-explainers over wetenschap', url: 'https://www.youtube.com/@kurzgesagt', color: '#f59e0b' },
        { name: 'Vox', desc: 'Data-driven uitlegvideo\'s', url: 'https://www.youtube.com/@Vox', color: '#ef4444' },
        { name: 'FF Wiki', desc: 'Onze eigen analyse & templates', url: 'https://wiki.fullframe.nl/doc/analyse-nos-op-3-explainers-shorts-aQw54E7PFe/edit', color: '#1e3a5f' },
      ].map(ch => (
        <Paper
          key={ch.name}
          component="a"
          href={ch.url}
          target="_blank"
          rel="noopener"
          sx={{
            p: 2, textDecoration: 'none', color: 'inherit',
            borderLeft: `4px solid ${ch.color}`, cursor: 'pointer',
            transition: 'all 0.2s', '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {ch.name === 'FF Wiki' ? <SchoolIcon sx={{ fontSize: 16 }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
            {ch.name}
            <OpenInNewIcon sx={{ fontSize: 12, ml: 'auto', opacity: 0.4 }} />
          </Typography>
          <Typography variant="caption" color="text.secondary">{ch.desc}</Typography>
        </Paper>
      ))}
    </Box>
  </Box>
);

// ─── Section Label ──────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <Typography variant="overline" sx={{
    color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem',
    letterSpacing: '0.08em', display: 'block', mb: 0.5,
  }}>
    {children}
  </Typography>
);

// ─── Script Tool Tab ─────────────────────────────────────────

const ScriptToolTab = () => {
  const [scriptText, setScriptText] = useState('');
  const [formality, setFormality] = useState(40);
  const [humor, setHumor] = useState(true);
  const [tone, setTone] = useState('mix');
  const [model, setModel] = useState('gpt');
  const [targetLength, setTargetLength] = useState(null);
  const [sameLength, setSameLength] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const wordCount = scriptText.trim() ? scriptText.trim().split(/\s+/).length : 0;
  const estimatedMinutes = Math.ceil(wordCount / 150);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setScriptText(ev.target.result);
      toast.success(`${file.name} geladen`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleAnalyze = async () => {
    if (!scriptText.trim()) { toast.error('Voer eerst een script in'); return; }
    setLoading(true);
    setResult(null);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/explainer-script/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          script_text: scriptText, formality, humor, tone, model,
          target_length: sameLength ? null : targetLength,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Analyse mislukt');
      }
      const data = await resp.json();
      setResult(data);
      toast.success('Script geanalyseerd!');
    } catch (e) {
      toast.error(e.message || 'Fout bij analyseren');
    } finally { setLoading(false); }
  };

  const copyRewritten = () => {
    if (!result?.paragraphs) return;
    const text = result.paragraphs.filter(p => p.change_type !== 'removed').map(p => p.rewritten).join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Herschreven script gekopieerd');
  };

  const downloadRewritten = () => {
    if (!result?.paragraphs) return;
    const text = result.paragraphs.filter(p => p.change_type !== 'removed').map(p => p.rewritten).join('\n\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'herschreven-script.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Input mode */}
      {!result && (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Script input */}
          <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{
              p: 3, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Script</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  {wordCount > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={`${wordCount} woorden`}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: '#f0f4ff', color: '#3b82f6' }}
                      />
                      <Chip
                        label={`~${estimatedMinutes} min`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Box>
                  )}
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".txt,.srt,.vtt" onChange={handleFileUpload} />
                  <Button
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                  >
                    Upload
                  </Button>
                </Box>
              </Box>
              <TextField
                multiline minRows={16} maxRows={32} fullWidth
                placeholder="Plak hier je script, voice-over tekst of treatment..."
                value={scriptText} onChange={e => setScriptText(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.7,
                    bgcolor: '#fafbfc',
                    borderRadius: 1.5,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb',
                  },
                }}
              />
            </Paper>
          </Box>

          {/* Parameters */}
          <Box sx={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Model Selection */}
            <Paper sx={{
              p: 2.5, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
            }}>
              <SectionLabel>AI Model</SectionLabel>
              <ToggleButtonGroup
                value={model}
                exclusive
                onChange={(_, v) => v && setModel(v)}
                size="small"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none', fontWeight: 600, gap: 0.5, py: 1,
                  },
                  '& .Mui-selected': {
                    bgcolor: model === 'gpt' ? '#e8f5e9 !important' : '#f3e8ff !important',
                    color: model === 'gpt' ? '#2e7d32 !important' : '#7c3aed !important',
                  },
                }}
              >
                <ToggleButton value="gpt">
                  <SmartToyIcon sx={{ fontSize: 18 }} />
                  GPT 5.2
                </ToggleButton>
                <ToggleButton value="claude">
                  <PsychologyIcon sx={{ fontSize: 18 }} />
                  Claude
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>

            {/* Style & Settings */}
            <Paper sx={{
              p: 2.5, borderRadius: 2, flex: 1,
              border: '1px solid', borderColor: 'divider',
            }}>
              <SectionLabel>Stijl</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                {[
                  { value: 'nos', label: 'NOS op 3', color: '#2563eb', bg: '#e8f0fe' },
                  { value: 'harris', label: 'J. Harris', color: '#7c3aed', bg: '#f3e8ff' },
                  { value: 'vox', label: 'Vox', color: '#ef4444', bg: '#fee2e2' },
                  { value: 'kurzgesagt', label: 'Kurzgesagt', color: '#d97706', bg: '#fef3c7' },
                  { value: 'mix', label: 'Mix', color: '#3b82f6', bg: '#f0f4ff' },
                ].map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    onClick={() => setTone(opt.value)}
                    sx={{
                      fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem',
                      border: '1.5px solid',
                      borderColor: tone === opt.value ? opt.color : 'transparent',
                      bgcolor: tone === opt.value ? opt.bg : '#f3f4f6',
                      color: tone === opt.value ? opt.color : 'text.secondary',
                      '&:hover': { bgcolor: opt.bg },
                    }}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <SectionLabel>Formaliteit</SectionLabel>
              <Box sx={{ px: 0.5, mb: 1 }}>
                <Chip
                  label={formality < 30 ? 'Informeel' : formality < 70 ? 'Conversationeel' : 'Formeel'}
                  size="small"
                  sx={{
                    fontWeight: 600, mb: 1, fontSize: '0.75rem',
                    bgcolor: formality < 30 ? '#fef3c7' : formality < 70 ? '#e8f0fe' : '#f3e8ff',
                    color: formality < 30 ? '#92400e' : formality < 70 ? '#1e40af' : '#5b21b6',
                  }}
                />
                <Slider
                  value={formality}
                  onChange={(_, v) => setFormality(v)}
                  min={0}
                  max={100}
                  sx={{ '& .MuiSlider-markLabel': { fontSize: '0.7rem', color: 'text.disabled' } }}
                  marks={[{ value: 0, label: 'Los' }, { value: 100, label: 'Strak' }]}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={humor} onChange={e => setHumor(e.target.checked)} size="small" />}
                  label={<Typography variant="body2" fontWeight={500}>Humor / ironie</Typography>}
                  sx={{ mx: 0 }}
                />
                <FormControlLabel
                  control={<Switch checked={sameLength} onChange={e => setSameLength(e.target.checked)} size="small" />}
                  label={<Typography variant="body2" fontWeight={500}>Zelfde lengte behouden</Typography>}
                  sx={{ mx: 0 }}
                />
              </Box>
              {!sameLength && (
                <TextField
                  type="number" size="small" label="Doellengte (woorden)"
                  value={targetLength || ''} onChange={e => setTargetLength(parseInt(e.target.value) || null)}
                  fullWidth sx={{ mt: 1.5 }}
                />
              )}
            </Paper>

            {/* Analyze Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              onClick={handleAnalyze}
              disabled={loading || !scriptText.trim()}
              sx={{
                py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '0.95rem',
                textTransform: 'none',
                background: loading
                  ? undefined
                  : 'linear-gradient(135deg, #3b82f6 0%, #7c3aed 100%)',
                boxShadow: loading ? undefined : '0 4px 14px 0 rgba(99, 102, 241, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #6d28d9 100%)',
                  boxShadow: '0 6px 20px 0 rgba(99, 102, 241, 0.45)',
                },
                '&.Mui-disabled': {
                  background: '#e5e7eb',
                },
              }}
            >
              {loading ? 'Bezig met analyseren...' : 'Analyseer & Herschrijf'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Results */}
      {result && (
        <Box>
          {/* Top bar */}
          <Paper sx={{
            p: 2, mb: 3, borderRadius: 2,
            border: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setResult(null)}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              Terug naar script
            </Button>

            {result.summary && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label={`${result.summary.original_word_count || wordCount} → ${result.summary.rewritten_word_count || '?'} woorden`}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: '#e8f0fe', color: '#1e40af' }}
                />
                <Chip
                  label={`~${Math.round((result.summary.estimated_duration_seconds || 0) / 60)} min`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={result.summary.tone_achieved || tone}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${result.summary.total_changes || 0} wijzigingen`}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e' }}
                />
              </Box>
            )}

            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={copyRewritten}
              sx={{ textTransform: 'none' }}
            >
              Kopieer
            </Button>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadRewritten}
              sx={{ textTransform: 'none' }}
            >
              Download
            </Button>
          </Paper>

          {/* Advice */}
          {result.advice?.length > 0 && (
            <Paper sx={{
              p: 2.5, mb: 3, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LightbulbIcon sx={{ color: '#f59e0b' }} /> Advies ({result.advice.length} punten)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {result.advice.map((a, i) => (
                  <Box key={i} sx={{
                    display: 'flex', gap: 1.5, alignItems: 'flex-start',
                    bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1.5, p: 1.5,
                  }}>
                    <Chip
                      label={a.category}
                      size="small"
                      sx={{
                        bgcolor: CATEGORY_COLORS[a.category] || '#6b7280',
                        color: '#fff', fontWeight: 600, minWidth: 90, mt: 0.1,
                      }}
                    />
                    <Typography variant="body2">{a.suggestion}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Side-by-side diff */}
          <Paper sx={{
            p: 2.5, borderRadius: 2,
            border: '1px solid', borderColor: 'divider',
          }}>
            <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CompareArrowsIcon color="primary" /> Vergelijking
            </Typography>
            <Box sx={{
              display: 'flex', gap: 2, mb: 2,
              pb: 1.5, borderBottom: '2px solid', borderColor: 'divider',
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>Origineel</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3b82f6' }}>Herschreven (explainer)</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {result.paragraphs?.map((p, i) => {
                const colors = CHANGE_COLORS[p.change_type] || CHANGE_COLORS.unchanged;
                return (
                  <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{
                      flex: 1, p: 2, borderRadius: 1.5, minHeight: 40,
                      bgcolor: p.change_type === 'added' ? '#f9fafb' : colors.bg,
                      borderLeft: p.change_type !== 'unchanged' ? `3px solid ${colors.border}` : '3px solid transparent',
                    }}>
                      {p.change_type === 'added' ? (
                        <Typography variant="body2" color="text.disabled" fontStyle="italic">(nieuw toegevoegd)</Typography>
                      ) : (
                        <Typography variant="body2" sx={{
                          whiteSpace: 'pre-wrap', lineHeight: 1.7,
                          textDecoration: p.change_type === 'removed' ? 'line-through' : 'none',
                          color: p.change_type === 'removed' ? 'text.disabled' : 'text.primary',
                        }}>
                          {p.original}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{
                      flex: 1, p: 2, borderRadius: 1.5, minHeight: 40,
                      bgcolor: p.change_type === 'removed' ? '#f9fafb' : colors.bg,
                      borderLeft: p.change_type !== 'unchanged' ? `3px solid ${colors.border}` : '3px solid transparent',
                    }}>
                      {p.change_type === 'removed' ? (
                        <Typography variant="body2" color="text.disabled" fontStyle="italic">(verwijderd)</Typography>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                            {p.rewritten}
                          </Typography>
                          {p.explanation && p.change_type !== 'unchanged' && (
                            <Typography variant="caption" sx={{
                              display: 'block', mt: 1.5, py: 0.5, px: 1,
                              bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 0.5,
                              color: colors.border, fontStyle: 'italic',
                            }}>
                              {p.explanation}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

// ─── Notes to Script Tab ────────────────────────────────────

const NotesToScriptTab = () => {
  const [notes, setNotes] = useState('');
  const [style, setStyle] = useState('nos');
  const [targetLength, setTargetLength] = useState('');
  const [focus, setFocus] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(null);

  const wordCount = notes.trim() ? notes.trim().split(/\s+/).length : 0;

  const fetchMeetings = async () => {
    setLoadingMeetings(true);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/explainer/meetings?limit=20`);
      if (!resp.ok) throw new Error('Ophalen mislukt');
      const data = await resp.json();
      setMeetings(data.meetings || []);
    } catch (e) {
      toast.error(e.message || 'Kon meetings niet laden');
    } finally { setLoadingMeetings(false); }
  };

  const loadTranscript = async (meetingId) => {
    setLoadingTranscript(meetingId);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/explainer/meetings/${meetingId}/transcript`);
      if (!resp.ok) throw new Error('Ophalen mislukt');
      const data = await resp.json();
      setNotes(data.transcript || '');
      setMeetings(null);
      toast.success(`"${data.title || 'Meeting'}" geladen`);
    } catch (e) {
      toast.error(e.message || 'Kon transcript niet laden');
    } finally { setLoadingTranscript(null); }
  };

  const handleGenerate = async () => {
    if (!notes.trim()) { toast.error('Voer eerst notes in'); return; }
    setLoading(true);
    setResult(null);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/explainer/notes-to-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: notes.trim(),
          style,
          target_length: targetLength ? parseInt(targetLength) : null,
          focus: focus.trim() || null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.details || err.error || 'Genereren mislukt');
      }
      const data = await resp.json();
      setResult(data);
      toast.success('Explainer-script gegenereerd!');
    } catch (e) {
      toast.error(e.message || 'Fout bij genereren');
    } finally { setLoading(false); }
  };

  const copyScript = () => {
    if (!result?.script) return;
    navigator.clipboard.writeText(result.script);
    toast.success('Script gekopieerd');
  };

  const downloadScript = () => {
    if (!result?.script) return;
    const filename = (result.title || 'explainer-script').replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_') + '.txt';
    const blob = new Blob([result.script], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Result View ──
  if (result) {
    return (
      <Box>
        <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Button variant="outlined" size="small" onClick={() => setResult(null)} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
            Terug
          </Button>
          <Chip label={result.title || 'Script'} sx={{ fontWeight: 600, bgcolor: '#e8f0fe', color: '#1e40af' }} />
          {result.word_count && <Chip label={`${result.word_count} woorden`} size="small" variant="outlined" />}
          {result.estimated_duration_seconds && (
            <Chip label={`~${Math.round(result.estimated_duration_seconds / 60)} min`} size="small" variant="outlined" />
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyScript} sx={{ textTransform: 'none' }}>
            Kopieer
          </Button>
          <Button size="small" startIcon={<DownloadIcon />} onClick={downloadScript} sx={{ textTransform: 'none' }}>
            Download
          </Button>
        </Paper>

        {/* Key topics */}
        {result.key_topics?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {result.key_topics.map((t, i) => (
              <Chip key={i} label={t} size="small" sx={{ bgcolor: '#f0f4ff', color: '#3b82f6', fontWeight: 500 }} />
            ))}
          </Box>
        )}

        {/* Hook highlight */}
        {result.hook && (
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1px solid #f59e0b33' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#92400e' }}>
              <LightbulbIcon sx={{ fontSize: 18, color: '#f59e0b' }} /> Hook
            </Typography>
            <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.7 }}>
              "{result.hook}"
            </Typography>
          </Paper>
        )}

        {/* Full script */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Script</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.88rem' }}>
            {result.script}
          </Typography>
        </Paper>

        {/* Visual suggestions */}
        {result.visual_suggestions?.length > 0 && (
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Beeld-suggesties</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {result.visual_suggestions.map((v, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Chip label={i + 1} size="small" sx={{ bgcolor: '#06b6d4', color: '#fff', fontWeight: 700, minWidth: 28 }} />
                  <Typography variant="body2">{v}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Notes from AI */}
        {result.notes && (
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Opmerkingen van AI</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{result.notes}</Typography>
          </Paper>
        )}
      </Box>
    );
  }

  // ── Input View ──
  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Notes input */}
      <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Meeting Notes</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {wordCount > 0 && (
                <Chip label={`${wordCount} woorden`} size="small" sx={{ fontWeight: 600, bgcolor: '#f0f4ff', color: '#3b82f6' }} />
              )}
              <Button
                size="small"
                startIcon={loadingMeetings ? <CircularProgress size={14} /> : <MicIcon />}
                onClick={fetchMeetings}
                disabled={loadingMeetings}
                sx={{ textTransform: 'none' }}
              >
                Importeer meeting
              </Button>
            </Box>
          </Box>

          {/* Meeting picker */}
          {meetings && (
            <Paper variant="outlined" sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
              {meetings.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  Geen meetings gevonden
                </Typography>
              ) : (
                meetings.map(m => (
                  <Box
                    key={m.id}
                    onClick={() => loadTranscript(m.id)}
                    sx={{
                      p: 1.5, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      '&:hover': { bgcolor: '#f8fafc' },
                      '&:last-child': { borderBottom: 'none' },
                      opacity: loadingTranscript === m.id ? 0.5 : 1,
                    }}
                  >
                    {loadingTranscript === m.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <MicIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{m.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.meeting_date ? new Date(m.meeting_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        {m.duration ? ` · ${m.duration} min` : ''}
                      </Typography>
                    </Box>
                    {!m.has_transcript && (
                      <Chip label="Geen transcript" size="small" sx={{ fontSize: '0.65rem' }} />
                    )}
                  </Box>
                ))
              )}
              <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                <Button size="small" onClick={() => setMeetings(null)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  Sluiten
                </Button>
              </Box>
            </Paper>
          )}

          <TextField
            multiline minRows={16} maxRows={32} fullWidth
            placeholder="Plak hier je meeting notes, vergadernotities, briefing of ruwe informatie..."
            value={notes} onChange={e => setNotes(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                fontSize: '0.85rem', lineHeight: 1.7, bgcolor: '#fafbfc', borderRadius: 1.5,
              },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
            }}
          />
        </Paper>
      </Box>

      {/* Settings panel */}
      <Box sx={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <SectionLabel>Explainer Stijl</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
            {[
              { value: 'nos', label: 'NOS op 3', color: '#2563eb', bg: '#e8f0fe' },
              { value: 'harris', label: 'J. Harris', color: '#7c3aed', bg: '#f3e8ff' },
              { value: 'vox', label: 'Vox', color: '#ef4444', bg: '#fee2e2' },
              { value: 'kurzgesagt', label: 'Kurzgesagt', color: '#d97706', bg: '#fef3c7' },
              { value: 'mix', label: 'Mix', color: '#3b82f6', bg: '#f0f4ff' },
            ].map(opt => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => setStyle(opt.value)}
                sx={{
                  fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem',
                  border: '1.5px solid',
                  borderColor: style === opt.value ? opt.color : 'transparent',
                  bgcolor: style === opt.value ? opt.bg : '#f3f4f6',
                  color: style === opt.value ? opt.color : 'text.secondary',
                  '&:hover': { bgcolor: opt.bg },
                }}
              />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <SectionLabel>Focus (optioneel)</SectionLabel>
          <TextField
            fullWidth size="small"
            placeholder="Bijv. 'duurzaamheid', 'werkprocessen'..."
            value={focus} onChange={e => setFocus(e.target.value)}
            sx={{ mb: 2 }}
          />

          <SectionLabel>Doellengte (optioneel)</SectionLabel>
          <TextField
            fullWidth size="small" type="number"
            placeholder="Bijv. 500, 1000, 1500..."
            value={targetLength} onChange={e => setTargetLength(e.target.value)}
            helperText={targetLength ? `~${Math.ceil(parseInt(targetLength) / 150)} minuten` : ''}
          />
        </Paper>

        <Button
          variant="contained" fullWidth size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArticleIcon />}
          onClick={handleGenerate}
          disabled={loading || !notes.trim()}
          sx={{
            py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '0.95rem', textTransform: 'none',
            background: loading ? undefined : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            boxShadow: loading ? undefined : '0 4px 14px 0 rgba(16, 185, 129, 0.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 6px 20px 0 rgba(16, 185, 129, 0.45)',
            },
            '&.Mui-disabled': { background: '#e5e7eb' },
          }}
        >
          {loading ? 'Script wordt gegenereerd...' : 'Genereer Explainer Script'}
        </Button>
      </Box>
    </Box>
  );
};

// ─── History Tab ─────────────────────────────────────────────
const HistoryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/explainer/history`);
      if (res.ok) setItems(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleDelete = async (id) => {
    if (!(await bevestig({ titel: 'Script verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/explainer/history/${id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Verwijderd');
    } catch { toast.error('Kon niet verwijderen'); }
  };

  if (selected) {
    let output;
    try { output = JSON.parse(selected.output_json); } catch { output = {}; }
    return (
      <Box>
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Button variant="outlined" size="small" onClick={() => setSelected(null)} sx={{ textTransform: 'none', borderRadius: 1.5 }}>Terug</Button>
          <Chip label={selected.title || 'Script'} sx={{ fontWeight: 600, bgcolor: '#e8f0fe', color: '#1e40af' }} />
          <Chip label={selected.user_name || selected.user_email} size="small" variant="outlined" />
          <Chip label={new Date(selected.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} size="small" variant="outlined" />
          <Chip label={selected.tab === 'notes_to_script' ? 'Notes → Script' : 'Herschreven'} size="small" color="info" />
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => { navigator.clipboard.writeText(output.script || ''); toast.success('Gekopieerd'); }} sx={{ textTransform: 'none' }}>Kopieer</Button>
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => {
            const text = output.script || '';
            const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `${(selected.title || 'script').replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_')}.txt`; a.click();
            URL.revokeObjectURL(url);
          }} sx={{ textTransform: 'none' }}>Download</Button>
        </Paper>
        {output.key_topics?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {output.key_topics.map((t, i) => <Chip key={i} label={t} size="small" sx={{ bgcolor: '#f0f4ff', color: '#3b82f6', fontWeight: 500 }} />)}
          </Box>
        )}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', lineHeight: 1.8, fontSize: '0.95rem' }}>
          {output.script || 'Geen script beschikbaar'}
        </Paper>
      </Box>
    );
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;
  if (items.length === 0) return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Nog geen opgeslagen scripts. Genereer een script via "Notes → Script" — het wordt automatisch opgeslagen.</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map(item => (
        <Paper key={item.id} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, '&:hover': { borderColor: 'primary.main', cursor: 'pointer' } }}
          onClick={() => setSelected(item)}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={600} noWrap>{item.title || 'Explainer script'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.user_name || item.user_email} — {new Date(item.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
          <Chip label={item.tab === 'notes_to_script' ? 'Notes → Script' : 'Herschreven'} size="small" color="info" variant="outlined" />
          <Chip label={item.style || 'mix'} size="small" variant="outlined" />
          <Button size="small" startIcon={<VisibilityIcon />} onClick={(e) => { e.stopPropagation(); setSelected(item); }} sx={{ textTransform: 'none' }}>Bekijk</Button>
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} sx={{ textTransform: 'none', minWidth: 'auto' }} />
        </Paper>
      ))}
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────

export default function ExplainerScript() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header */}
      <PageHeader
        icon="auto_fix_high"
        title="Explainer Studio"
        subtitle="Leer, schrijf en verbeter explainer-scripts"
        accent={{ color: '#635983', bg: '#dbcdff' }}
      />

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<SchoolIcon />} iconPosition="start" label="Leren & Inspiratie" />
        <Tab icon={<AutoFixHighIcon />} iconPosition="start" label="Script Herschrijven" />
        <Tab icon={<ArticleIcon />} iconPosition="start" label="Notes → Script" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Geschiedenis" />
      </Tabs>

      {tab === 0 && <LearnTab />}
      {tab === 1 && <ScriptToolTab />}
      {tab === 2 && <NotesToScriptTab />}
      {tab === 3 && <HistoryTab />}
    </Box>
  );
}
